/**
 * Registering the service worker and subscribing to Web Push (brief §16).
 *
 * Only for notifications **without** a place in them — somebody reporting in your home
 * region, your report reaching three confirmations. Everything location-based is decided on
 * the device by `alerts.svelte.ts` and never involves a server.
 */

import { request } from './api.ts';
import { watchForUpdates } from './update.ts';

export type PushState = 'unsupported' | 'unavailable' | 'denied' | 'off' | 'on';

const supported = () =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof PushManager !== 'undefined' &&
  typeof Notification !== 'undefined';

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
  try {
    // Scope `/` so a notification click can focus the map rather than opening a second tab.
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    watchForUpdates(registration);
    return registration;
  } catch {
    // A failed registration costs push and nothing else. The map, the reporting and the
    // local alerts all work without it.
    return undefined;
  }
}

/**
 * base64url to the `Uint8Array` `PushManager.subscribe` insists on.
 *
 * Hand-rolled because it is nine lines and the alternative is a dependency for nine lines.
 * The `+`/`/` swap is the part that matters: VAPID keys are base64**url**, and feeding the
 * raw string to `atob` produces a key that is accepted and then never decrypts anything.
 */
function decodeKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  // The buffer is allocated explicitly so the view is a `Uint8Array<ArrayBuffer>` and not a
  // `Uint8Array<ArrayBufferLike>`, which `applicationServerKey` does not accept — it might be
  // backed by a SharedArrayBuffer as far as the type system knows.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export async function pushState(): Promise<PushState> {
  if (!supported()) return 'unsupported';
  const { publicKey } = await request<{ publicKey: string | null }>('/api/push/vapid');
  if (publicKey === null) return 'unavailable';
  if (Notification.permission === 'denied') return 'denied';

  const registration = await navigator.serviceWorker.getRegistration();
  const existing = await registration?.pushManager.getSubscription();
  return existing === null || existing === undefined ? 'off' : 'on';
}

export async function enablePush(): Promise<PushState> {
  if (!supported()) return 'unsupported';
  const { publicKey } = await request<{ publicKey: string | null }>('/api/push/vapid');
  if (publicKey === null) return 'unavailable';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';

  const registration = (await registerServiceWorker()) ?? undefined;
  if (registration === undefined) return 'unsupported';
  await navigator.serviceWorker.ready;

  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Required by every browser that implements it: a push that cannot be shown is not
      // allowed to be silent.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(publicKey),
    }));

  const json = subscription.toJSON();
  await request('/api/push/subscription', {
    method: 'POST',
    body: JSON.stringify({ endpoint: subscription.endpoint, keys: json.keys }),
  });
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  if (!supported()) return 'unsupported';
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription == null) return 'off';

  // Tell the server first. Unsubscribing locally and then failing to reach the server leaves
  // a row that will be pushed to for ever and answered by nobody.
  await request('/api/push/subscription', {
    method: 'DELETE',
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  }).catch(() => undefined);
  await subscription.unsubscribe();
  return 'off';
}

/** Ask for permission to show the *local* geofence alerts. No server, no subscription. */
export async function askLocalNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}
